import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  AlertTriangle, ArrowUp, BarChart3, ChevronDown, Lightbulb, Loader2, MessageSquareText, RefreshCw, RotateCcw,
  ShieldCheck, ThumbsUp, TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import { AuroraLogo } from '../../office/icons';
import { markdownToHtml } from '../../office/markdown';
import { mensajeDe } from '../responder/utilidades';
import './analisis.css';

/*
 * Pestaña "Aurora IA" de las respuestas: el informe (panorama, hallazgos, quejas que se repiten, dónde sale
 * peor cada calificación, temas de cada pregunta abierta) y una conversación para preguntar lo que sea.
 *
 * Reparto a propósito (ver backend/formularios.py): Aurora LEE y redacta; las cifras las calcula el servidor.
 * Por eso cada respuesta de la conversación puede enseñar «Cómo lo calculé» con la tabla exacta.
 *
 * API: GET/POST /forms/{id}/analisis · POST /forms/{id}/analisis/preguntar {pregunta, historial}
 */

const PASOS = ['Leyendo las respuestas…', 'Agrupando los comentarios en temas…', 'Buscando dónde sale peor cada calificación…', 'Redactando los hallazgos…'];

const TIPO_HALLAZGO = {
  alerta: { Icono: AlertTriangle, nombre: 'Alerta' },
  oportunidad: { Icono: Lightbulb, nombre: 'Oportunidad' },
  positivo: { Icono: ThumbsUp, nombre: 'Lo que va bien' },
  dato: { Icono: BarChart3, nombre: 'Dato' },
};

const hace = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es });
  } catch {
    return '';
  }
};

const numero = (v) => (typeof v === 'number' ? v.toLocaleString('es-MX', { maximumFractionDigits: 2 }) : v);

/** Ejemplos (citas) que se abren al pulsar: son de las respuestas, tal cual. */
function Ejemplos({ ejemplos }) {
  if (!ejemplos?.length) return null;
  return (
    <details className="ra-ejemplos">
      <summary>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> Ver ejemplos
      </summary>
      <ul>
        {ejemplos.map((e) => <li key={e}>«{e}»</li>)}
      </ul>
    </details>
  );
}

function Barra({ porcentaje, tono = 'tema' }) {
  return (
    <span className={`ra-barra is-${tono}`} aria-hidden="true">
      <span style={{ width: `${Math.max(2, Math.min(100, porcentaje || 0))}%` }} />
    </span>
  );
}

function TablaConsulta({ consulta }) {
  return (
    <div className="ra-tabla">
      <div className="ra-tabla-titulo">{consulta.descripcion} · {consulta.n} respuestas</div>
      <div className="ra-tabla-scroll">
        <table>
          <thead>
            <tr>{consulta.columnas.map((c) => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {consulta.filas.map((fila, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>{fila.map((celda, j) => <td key={j}>{numero(celda)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================== Conversación ============================== */

function Conversacion({ formId, sugeridas }) {
  const [mensajes, setMensajes] = useState([]); // {rol: 'usuario'|'aurora', texto, consultas, error}
  const [texto, setTexto] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const listaRef = useRef(null);
  const campoRef = useRef(null);

  useEffect(() => {
    const lista = listaRef.current;
    if (lista) lista.scrollTo({ top: lista.scrollHeight, behavior: 'smooth' });
  }, [mensajes, ocupado]);

  const preguntar = async (valor) => {
    const pregunta = (valor ?? texto).trim();
    if (!pregunta || ocupado) return;
    const historial = mensajes.filter((m) => !m.error).map(({ rol, texto: t }) => ({ rol, texto: t }));
    setMensajes((m) => [...m, { rol: 'usuario', texto: pregunta }]);
    setTexto('');
    setOcupado(true);
    try {
      const { data } = await api.post(`/forms/${formId}/analisis/preguntar`, { pregunta, historial }, { timeout: 120000 });
      setMensajes((m) => [...m, { rol: 'aurora', texto: data.respuesta || 'No encontré una respuesta.', consultas: data.consultas || [] }]);
    } catch (err) {
      setMensajes((m) => [...m, { rol: 'aurora', texto: mensajeDe(err, 'Aurora IA no pudo responder. Intenta de nuevo.'), error: true }]);
    } finally {
      setOcupado(false);
      campoRef.current?.focus();
    }
  };

  return (
    <section className="rs-tarjeta ra-chat" aria-label="Pregúntale a Aurora">
      <div className="ra-chat-cabeza">
        <h3 className="rs-tarjeta-titulo"><MessageSquareText className="h-[18px] w-[18px]" aria-hidden="true" /> Pregúntale a Aurora</h3>
        {mensajes.length > 0 && (
          <button type="button" className="fm-boton-texto ra-chat-nueva" onClick={() => setMensajes([])} disabled={ocupado}>
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Nueva conversación
          </button>
        )}
      </div>
      <p className="rs-vacio">Pregunta con tus palabras: «¿qué zona tiene peor calificación?», «¿de qué se quejan en el turno nocturno?», «¿cuántas respuestas llegaron esta semana?».</p>

      {mensajes.length === 0 && sugeridas?.length > 0 && (
        <div className="ra-sugeridas">
          {sugeridas.map((s) => (
            <button key={s} type="button" className="ra-sugerida" onClick={() => preguntar(s)} disabled={ocupado}>{s}</button>
          ))}
        </div>
      )}

      {(mensajes.length > 0 || ocupado) && (
        <div className="ra-mensajes fm-scroll" ref={listaRef} aria-live="polite">
          {mensajes.map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className={`ra-mensaje is-${m.rol} ${m.error ? 'is-error' : ''}`}>
              {m.rol === 'aurora' && <span className="ra-mensaje-avatar"><AuroraLogo size={18} /></span>}
              <div className="ra-mensaje-cuerpo">
                {m.rol === 'aurora' && !m.error
                  // eslint-disable-next-line react/no-danger
                  ? <div className="ra-md" dangerouslySetInnerHTML={{ __html: markdownToHtml(m.texto) }} />
                  : <p>{m.texto}</p>}
                {m.consultas?.length > 0 && (
                  <details className="ra-calculo">
                    <summary><ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> Cómo lo calculé</summary>
                    {m.consultas.map((c) => <TablaConsulta key={c.descripcion} consulta={c} />)}
                  </details>
                )}
              </div>
            </div>
          ))}
          {ocupado && (
            <div className="ra-mensaje is-aurora">
              <span className="ra-mensaje-avatar"><AuroraLogo size={18} /></span>
              <div className="ra-mensaje-cuerpo ra-pensando"><Loader2 className="h-4 w-4 od-spin" aria-hidden="true" /> Revisando las respuestas…</div>
            </div>
          )}
        </div>
      )}

      <form
        className="ra-entrada"
        onSubmit={(e) => {
          e.preventDefault();
          preguntar();
        }}
      >
        <textarea
          ref={campoRef}
          rows={1}
          value={texto}
          maxLength={1000}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              preguntar();
            }
          }}
          placeholder="Escribe una pregunta sobre las respuestas…"
          aria-label="Pregunta para Aurora"
          disabled={ocupado}
        />
        <button type="submit" className="ra-enviar" disabled={ocupado || !texto.trim()} aria-label="Preguntar">
          {ocupado ? <Loader2 className="h-4 w-4 od-spin" /> : <ArrowUp className="h-4 w-4" />}
        </button>
      </form>
    </section>
  );
}

/* ============================== Informe ============================== */

function Hallazgos({ hallazgos }) {
  if (!hallazgos?.length) return null;
  return (
    <div className="ra-hallazgos">
      {hallazgos.map((h) => {
        const tipo = TIPO_HALLAZGO[h.tipo] || TIPO_HALLAZGO.dato;
        const { Icono } = tipo;
        return (
          <article key={h.titulo} className={`ra-hallazgo is-${h.tipo}`}>
            <span className="ra-hallazgo-icono"><Icono className="h-4 w-4" aria-hidden="true" /></span>
            <div>
              <div className="ra-hallazgo-tipo">{tipo.nombre}</div>
              <h4>{h.titulo}</h4>
              <p>{h.detalle}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function Quejas({ quejas }) {
  if (!quejas?.length) return null;
  return (
    <section className="rs-tarjeta">
      <h3 className="rs-tarjeta-titulo ra-titulo-icono"><AlertTriangle className="h-[18px] w-[18px]" aria-hidden="true" /> Quejas y problemas que se repiten</h3>
      <ul className="ra-lista">
        {quejas.map((q) => (
          <li key={`${q.campo}-${q.tema}`}>
            <div className="ra-fila">
              <div className="ra-fila-texto">
                <strong>{q.tema}</strong>
                {q.descripcion && <span>{q.descripcion}</span>}
                <span className="ra-origen">En «{q.titulo}»</span>
              </div>
              <div className="ra-fila-cifra">
                <strong>{q.n}</strong> {q.n === 1 ? 'respuesta' : 'respuestas'} · {numero(q.porcentaje)}%
              </div>
            </div>
            <Barra porcentaje={q.porcentaje} tono="negativo" />
            <Ejemplos ejemplos={q.ejemplos} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function PuntosBajos({ puntos }) {
  if (!puntos?.length) return null;
  return (
    <section className="rs-tarjeta">
      <h3 className="rs-tarjeta-titulo ra-titulo-icono"><TrendingDown className="h-[18px] w-[18px]" aria-hidden="true" /> Dónde sale peor cada calificación</h3>
      <p className="rs-vacio">Promedios calculados con las respuestas; cada grupo necesita al menos 2 respuestas.</p>
      <ul className="ra-lista">
        {puntos.map((p) => {
          const tope = p.max ?? Math.max(p.mejor.valor, 1);
          const base = p.min ?? 0;
          const pct = (v) => (100 * (v - base)) / ((tope - base) || 1);
          return (
            <li key={`${p.grupo_campo}-${p.medida}`}>
              <div className="ra-origen">«{p.medida}» por «{p.grupo_campo}»</div>
              <div className="ra-comparacion">
                <div className="ra-comparacion-fila is-peor">
                  <span className="ra-comparacion-grupo">{p.peor.grupo}</span>
                  <Barra porcentaje={pct(p.peor.valor)} tono="negativo" />
                  <span className="ra-comparacion-valor">
                    <strong>{numero(p.peor.valor)}</strong>{p.max != null && ` de ${p.max}`} · {p.peor.n} resp.
                  </span>
                </div>
                <div className="ra-comparacion-fila is-mejor">
                  <span className="ra-comparacion-grupo">{p.mejor.grupo}</span>
                  <Barra porcentaje={pct(p.mejor.valor)} tono="positivo" />
                  <span className="ra-comparacion-valor">
                    <strong>{numero(p.mejor.valor)}</strong>{p.max != null && ` de ${p.max}`} · {p.mejor.n} resp.
                  </span>
                </div>
              </div>
              {p.peor.n < 3 && <p className="ra-aviso-pocas">Pocas respuestas en {p.peor.grupo}: tómalo con cautela.</p>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PreguntaAbierta({ texto }) {
  const s = texto.sentimiento || {};
  const suma = (s.positivo || 0) + (s.neutral || 0) + (s.negativo || 0);
  const pct = (n) => (suma ? (100 * n) / suma : 0);
  return (
    <section className="rs-tarjeta">
      <h3 className="rs-tarjeta-titulo">{texto.titulo}</h3>
      <div className="rs-tarjeta-meta">
        {texto.analizadas < texto.total
          ? <span>Se leyeron las {texto.analizadas} respuestas más recientes de {texto.total}</span>
          : <span>{texto.analizadas} respuestas leídas</span>}
      </div>
      {texto.error && <p className="rs-vacio">Aurora no pudo leer esta pregunta. Actualiza el análisis para intentarlo de nuevo.</p>}
      {texto.resumen && <p className="ra-parrafo">{texto.resumen}</p>}
      {suma > 0 && (
        <div className="ra-sentimiento">
          <div className="ra-sentimiento-barra" role="img" aria-label={`Positivas ${s.positivo || 0}, neutras ${s.neutral || 0}, negativas ${s.negativo || 0}`}>
            <span className="is-positivo" style={{ width: `${pct(s.positivo || 0)}%` }} />
            <span className="is-neutral" style={{ width: `${pct(s.neutral || 0)}%` }} />
            <span className="is-negativo" style={{ width: `${pct(s.negativo || 0)}%` }} />
          </div>
          <div className="ra-sentimiento-leyenda">
            <span><i className="is-positivo" /> Positivas {s.positivo || 0}</span>
            <span><i className="is-neutral" /> Neutras {s.neutral || 0}</span>
            <span><i className="is-negativo" /> Negativas {s.negativo || 0}</span>
          </div>
        </div>
      )}
      {texto.temas?.length > 0 && (
        <>
          <span className="rs-subtitulo">Temas</span>
          <ul className="ra-lista">
            {texto.temas.map((t) => (
              <li key={t.nombre}>
                <div className="ra-fila">
                  <div className="ra-fila-texto">
                    <strong>
                      {t.nombre}
                      {t.es_queja && <span className="ra-etiqueta is-queja">Queja</span>}
                      {!t.es_queja && t.sentimiento === 'positivo' && <span className="ra-etiqueta is-positivo">Positivo</span>}
                    </strong>
                    {t.descripcion && <span>{t.descripcion}</span>}
                  </div>
                  <div className="ra-fila-cifra"><strong>{t.n}</strong> · {numero(t.porcentaje)}%</div>
                </div>
                <Barra porcentaje={t.porcentaje} tono={t.es_queja || t.sentimiento === 'negativo' ? 'negativo' : 'tema'} />
                <Ejemplos ejemplos={t.ejemplos} />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/* ============================== Pestaña ============================== */

export default function AnalisisAurora({ formId, total }) {
  const [estado, setEstado] = useState({ cargando: true, analisis: null, desactualizado: false, error: null });
  const [generando, setGenerando] = useState(false);
  const [paso, setPaso] = useState(0);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get(`/forms/${formId}/analisis`);
      setEstado({ cargando: false, analisis: data.analisis, desactualizado: !!data.desactualizado, error: null });
    } catch (err) {
      setEstado((e) => ({ ...e, cargando: false, error: mensajeDe(err, 'No se pudo cargar el análisis') }));
    }
  }, [formId]);

  // También al llegar respuestas nuevas: así aparece el aviso de "desactualizado" sin recargar
  useEffect(() => { cargar(); }, [cargar, total]);

  useEffect(() => {
    if (!generando) return undefined;
    setPaso(0);
    const t = setInterval(() => setPaso((p) => Math.min(p + 1, PASOS.length - 1)), 3500);
    return () => clearInterval(t);
  }, [generando]);

  const analizar = async () => {
    setGenerando(true);
    try {
      const { data } = await api.post(`/forms/${formId}/analisis`, {}, { timeout: 240000 });
      setEstado({ cargando: false, analisis: data.analisis, desactualizado: false, error: null });
    } catch (err) {
      toast.error(mensajeDe(err, 'Aurora IA no pudo analizar las respuestas'));
    } finally {
      setGenerando(false);
    }
  };

  const a = estado.analisis;
  const sugeridas = useMemo(() => a?.preguntas_sugeridas || [], [a]);

  if (estado.cargando) {
    return <div className="rs-cargando"><Loader2 className="h-7 w-7 od-spin" aria-label="Cargando análisis" /></div>;
  }

  if (generando) {
    return (
      <section className="rs-tarjeta ra-generando" aria-live="polite">
        <span className="ra-portada-logo is-latiendo"><AuroraLogo size={34} /></span>
        <h3>{PASOS[paso]}</h3>
        <p className="rs-vacio">Aurora está leyendo {total} {total === 1 ? 'respuesta' : 'respuestas'}. Tarda unos segundos.</p>
        <div className="ra-esqueleto" aria-hidden="true"><span /><span /><span /></div>
      </section>
    );
  }

  if (!a) {
    return (
      <section className="rs-tarjeta ra-portada">
        <span className="ra-portada-logo"><AuroraLogo size={40} /></span>
        <h3>Aurora IA lee tus respuestas por ti</h3>
        <p>
          Resume los comentarios abiertos, encuentra las quejas que más se repiten y dónde sale peor cada
          calificación. Después puedes preguntarle lo que quieras sobre las respuestas.
        </p>
        {estado.error && <p className="rs-vacio">{estado.error}</p>}
        <button type="button" className="fm-boton" onClick={analizar}>
          <AuroraLogo size={16} mono /> Analizar {total} {total === 1 ? 'respuesta' : 'respuestas'}
        </button>
        <p className="ra-privacidad">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          Aurora no recibe correos ni archivos, y no lee preguntas de datos personales (nombre, teléfono, dirección…).
        </p>
      </section>
    );
  }

  return (
    <div className="ra-informe">
      <section className="rs-tarjeta ra-panorama">
        <div className="ra-panorama-cabeza">
          <span className="ra-portada-logo is-chico"><AuroraLogo size={22} /></span>
          <div className="ra-panorama-titulo">
            <h3 className="rs-tarjeta-titulo">Análisis de Aurora IA</h3>
            <div className="rs-tarjeta-meta">
              <span>{a.n} {a.n === 1 ? 'respuesta analizada' : 'respuestas analizadas'}</span>
              {a.generado_en && <span>· {hace(a.generado_en)}</span>}
              {a.generado_por && <span>· {a.generado_por}</span>}
            </div>
          </div>
          <button type="button" className="fm-boton-texto ra-actualizar" onClick={analizar} title="Volver a analizar con todas las respuestas" aria-label="Actualizar el análisis">
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> <span>Actualizar</span>
          </button>
        </div>
        {estado.desactualizado && (
          <div className="ra-desactualizado" role="status">
            <span>Llegaron o cambiaron respuestas desde este análisis.</span>
            <button type="button" className="fm-boton-texto" onClick={analizar}>Actualizar ahora</button>
          </div>
        )}
        {a.resumen && <p className="ra-parrafo ra-resumen">{a.resumen}</p>}
        <Hallazgos hallazgos={a.hallazgos} />
      </section>

      <Conversacion formId={formId} sugeridas={sugeridas} />
      <Quejas quejas={a.quejas} />
      <PuntosBajos puntos={a.puntos_bajos} />
      {(a.textos || []).map((t) => <PreguntaAbierta key={t.campo} texto={t} />)}

      {a.recomendaciones?.length > 0 && (
        <section className="rs-tarjeta">
          <h3 className="rs-tarjeta-titulo ra-titulo-icono"><Lightbulb className="h-[18px] w-[18px]" aria-hidden="true" /> Recomendaciones</h3>
          <ol className="ra-recomendaciones">
            {a.recomendaciones.map((r) => <li key={r}>{r}</li>)}
          </ol>
        </section>
      )}

      <p className="ra-nota">
        Las cifras se calculan directamente de las respuestas. Los temas y los textos los redacta Aurora IA
        leyendo los comentarios y puede equivocarse: revisa los ejemplos antes de decidir.
      </p>
    </div>
  );
}
